<script setup lang="ts">
const { items = [] } = defineProps<{
  items?: IRichData[];
}>();

// New entry types may ship on the API before the widget knows how to render
// them - skip anything we don't recognise instead of breaking the message.
const KNOWN_TYPES: RichDataType[] = ["product", "link"];

type RichDataCard = {
  key: string;
  type: RichDataType;
  title: string;
  url: string;
  image?: string;
  meta?: string;
  description?: string;
};

const typeLabel: Record<RichDataType, string> = {
  product: "Product",
  link: "Link",
};

const cards = computed<RichDataCard[]>(() =>
  (items ?? [])
    .filter((item) => item && KNOWN_TYPES.includes(item.type))
    .map((item, index) => ({
      key: `${item.type}-${item.url}-${index}`,
      type: item.type,
      title: item.title,
      url: item.url,
      image: item.image,
      meta: item.type === "product" ? item.price : undefined,
      description: item.description,
    })),
);
</script>

<template>
  <div v-if="cards.length" class="chat-rich-data">
    <a
      v-for="card in cards"
      :key="card.key"
      class="rich-data-card"
      :href="card.url"
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        v-if="card.image"
        :src="card.image"
        :alt="card.title"
        class="image"
        loading="lazy"
      />
      <div class="body">
        <span class="badge" :class="`is-${card.type}`">{{
          typeLabel[card.type]
        }}</span>
        <div class="title">{{ card.title }}</div>
        <div v-if="card.meta" class="meta">{{ card.meta }}</div>
        <div v-if="card.description" class="description">
          {{ card.description }}
        </div>
      </div>
      <BaseIcon name="open_in_new" class="icon" />
    </a>
  </div>
</template>

<style lang="scss" scoped>
.chat-rich-data {
  display: flex;
  flex-direction: column;
  // Flex items default to min-width: auto, which refuses to shrink below
  // its content's natural width - without this, a long card can widen the
  // whole chat bubble instead of truncating.
  min-width: 0;
  gap: 0.5rem;
}

.rich-data-card {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
  border-radius: calc(var(--main-border-radius) / 1.5);
  background: rgb(from currentcolor r g b / 0.06);
  color: inherit;
  text-decoration: none;
  overflow-wrap: anywhere;
  padding: 0.6rem;

  &:hover {
    background: rgb(from currentcolor r g b / 0.12);
  }
}

.image {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: calc(var(--main-border-radius) / 2);
  object-fit: cover;
}

.body {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-width: 0;
  gap: 0.15em;
}

.badge {
  align-self: flex-start;
  border-radius: 4px;
  background: rgb(from currentcolor r g b / 0.12);
  font-weight: 600;
  font-size: 0.7em;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.05em 0.5em;
}

.title {
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.meta {
  font-weight: 600;
  font-size: 0.9em;
  opacity: 0.85;
}

.description {
  font-size: 0.85em;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  opacity: 0.75;
}

.icon {
  flex-shrink: 0;
  align-self: center;
  font-size: 20px;
  opacity: 0.6;
}
</style>
